import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { Toaster } from '@/components/ui/sonner';
import { CartProvider } from '@/context/CartContext';
import AgeGate from '@/components/AgeGate';
import Faq from '@/pages/Faq';
import CategoryLanding from '@/pages/CategoryLanding';
import { MONEY_PAGES } from '@/data/moneyPages';
import { SEO } from '@/lib/seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { EcwidMinicart, EcwidStoreShell } from '@/components/EcwidWidgets';
import Home from '@/pages/Home';
import Shop from '@/pages/Shop';
import Deals from '@/pages/Deals';
import ServiceArea from '@/pages/ServiceArea';
import HowToOrder from '@/pages/HowToOrder';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import LocationPage from '@/pages/LocationPage';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import { LOCATIONS, HUB_PAGES } from '@/data/locations';
import '@/App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.start();
      window.__lenis.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div data-testid="not-found-page" className="mx-auto max-w-2xl px-6 pb-28 pt-40 text-center">
      <h1 className="font-serif text-5xl text-ink">Page not found</h1>
      <p className="mt-4 text-sm text-sage">The page you're looking for doesn't exist.</p>
      <a href="/" data-testid="not-found-home-link" className="mt-8 inline-block bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-ink hover:bg-bronzehover">
        Back Home
      </a>
    </div>
  );
}

function App() {
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/`).catch(() => {});
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <ScrollToTop />
          <AgeGate />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:category" element={<Shop />} />
              <Route path="/deals" element={<Deals />} />
              <Route path="/service-area" element={<ServiceArea />} />
              <Route path="/how-to-order" element={<HowToOrder />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<Faq />} />
              {MONEY_PAGES.map((p) => (
                <Route key={p.slug} path={`/${p.slug}`} element={<CategoryLanding page={p} />} />
              ))}
              <Route path="/checkout" element={<><SEO title="Checkout | Majestic Greens DMV" path="/checkout" noindex /><div className="pt-28" data-testid="checkout-page" /></>} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              {LOCATIONS.map((loc) => (
                <Route key={loc.slug} path={`/${loc.slug}`} element={<LocationPage location={loc} />} />
              ))}
              {HUB_PAGES.map((loc) => (
                <Route key={loc.slug} path={`/${loc.slug}`} element={<LocationPage location={loc} />} />
              ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <EcwidStoreShell />
          <Footer />
          <EcwidMinicart />
          <Toaster theme="light" position="top-center" toastOptions={{ duration: 2500 }} />
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
